import {
    useCallback,
    useEffect,
    useState,
    useRef,
    FC,
    ChangeEvent,
} from 'react';

import debounce from 'lodash/debounce';
import { Check, ChevronDown, Search, Plus } from 'lucide-react';

import { OfferDraftFormValues } from './offerDraft';
import { Badge } from '../../../../components/ui/Badge';
import { Image } from '../../../../components/ui/Image';
import { Input } from '../../../../components/ui/Input';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '../../../../components/ui/Popover';
import { Routes } from '../../../../models/routes';
import { CartonProduct, DuplicateWarning } from '../../../../types';
import { SetData } from '../../../../types/shared';
import { cn } from '../../../../utils';
import { ProductProposalModal } from '../New/ProductProposalModal';

interface Props {
    setData: SetData<OfferDraftFormValues>;
    setSelected: (product: CartonProduct) => Record<string, string> | void;
    initialOptions: CartonProduct[];
    selectedProduct: CartonProduct | null;
    searchSeed?: string | null;
    selectedUniverseId?: number | null;
    selectedProductTypeId?: number | null;
    onDuplicateCheck?: (data: DuplicateWarning) => void;
    isEditing?: boolean;
    placeholder?: string;
}

const DEFAULT_PX_OFFSET = 16;

export const ProductSelector: FC<Props> = ({
    setData,
    setSelected,
    initialOptions,
    selectedProduct,
    searchSeed,
    selectedUniverseId,
    selectedProductTypeId,
    onDuplicateCheck,
    isEditing = false,
    placeholder = 'Оберіть товар',
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [options, setOptions] = useState<CartonProduct[]>([]);
    const [open, setOpen] = useState(false);
    const [preview, setPreview] = useState<{
        product: CartonProduct;
        offset: {
            x: number;
            y: number;
        };
    } | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    const [isProposalOpen, setIsProposalOpen] = useState(false);

    const openProposalModal = () => {
        setIsProposalOpen(true);
    };

    const fetchOptions = async (
        query: string,
        universeId?: number | null,
        productTypeId?: number | null,
    ) => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        const signal = abortController.signal;

        const queryParams = new URLSearchParams({
            q: query,
        });

        if (universeId) {
            queryParams.set('universe_id', universeId.toString());
        }

        if (productTypeId) {
            queryParams.set('product_type_id', productTypeId.toString());
        }

        try {
            const res = await fetch(
                `${Routes.ProductLookup}?${queryParams.toString()}`,
                { signal },
            );
            const data = await res.json();
            if (!signal.aborted) {
                setOptions(data);
            }
        } catch (err) {
            console.error('Fetch error:', err);
        }
    };

    const debouncedFetch = useCallback(
        debounce(
            (
                val: string,
                universeId?: number | null,
                productTypeId?: number | null,
            ) => {
                fetchOptions(val, universeId, productTypeId);
            },
            800,
        ),
        [],
    );

    const checkExistingOffer = async (productId: string) => {
        try {
            const response = await fetch(
                `${Routes.Offers}/check_existing?product_id=${productId}`,
            );
            const data = await response.json();

            if (data.exists && onDuplicateCheck) {
                onDuplicateCheck(data);
            }
        } catch (error) {
            console.error('Error checking existing offer:', error);
        }
    };

    function handleProductChange(product: CartonProduct) {
        const nextPropertySet = setSelected(product);

        setData('product_id', product.id.toString());

        if (nextPropertySet !== undefined) {
            setData('property_set', nextPropertySet);
        }

        checkExistingOffer(product.id.toString());
        setOpen(false);
    }

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;

        setSearchTerm(value);

        if (value && value.length > 2) {
            debouncedFetch(
                e.target.value,
                selectedUniverseId,
                selectedProductTypeId,
            );
        }
    };

    const showProductImage = (
        product: CartonProduct,
        offset: { x: number; y: number },
    ) => {
        setPreview({
            product,
            offset,
        });
    };

    const contentRef = useRef<HTMLDivElement | null>(null);
    const normalizedSearchSeed = searchSeed?.trim() ?? '';

    useEffect(() => {
        const nextOptions = initialOptions.filter((option) => {
            const matchesUniverse = selectedUniverseId
                ? option.universe_id === selectedUniverseId
                : true;
            const matchesProductType = selectedProductTypeId
                ? option.product_type_id === selectedProductTypeId
                : true;

            return matchesUniverse && matchesProductType;
        });

        setOptions(nextOptions);
        setSearchTerm('');
        debouncedFetch.cancel();
        if (selectedUniverseId) {
            fetchOptions('', selectedUniverseId, selectedProductTypeId);
        }

        return () => {
            debouncedFetch.cancel();
        };
    }, [
        selectedUniverseId,
        selectedProductTypeId,
        initialOptions,
        debouncedFetch,
    ]);

    useEffect(() => {
        if (!open || !normalizedSearchSeed) return;

        debouncedFetch.cancel();
        setSearchTerm(normalizedSearchSeed);
        fetchOptions(
            normalizedSearchSeed,
            selectedUniverseId,
            selectedProductTypeId,
        );
    }, [
        debouncedFetch,
        normalizedSearchSeed,
        open,
        selectedProductTypeId,
        selectedUniverseId,
    ]);

    return (
        <>
            {preview && (
                <img
                    data-testid="product-image-preview"
                    className="pointer-events-none fixed z-[130] h-[350px] w-[250px] rounded-2xl"
                    style={{
                        top: `${preview.offset.y + DEFAULT_PX_OFFSET}px`,
                        left: `${preview.offset.x + DEFAULT_PX_OFFSET}px`,
                    }}
                    src={preview.product.image}
                    alt={preview.product.image_alt}
                />
            )}
            <Popover open={open} onOpenChange={setOpen}>
                {!isEditing && (
                    <PopoverTrigger asChild>
                        <button
                            id="product-select"
                            className={cn(
                                'flex h-8 w-full items-center justify-between rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 sm:h-9 [&>span]:line-clamp-1',
                            )}
                            disabled={isEditing}
                        >
                            <span
                                className={cn(
                                    'line-clamp-1',
                                    !selectedProduct && 'text-muted-foreground',
                                )}
                            >
                                {selectedProduct?.name || placeholder}
                            </span>
                            <ChevronDown className="h-4 w-4 opacity-50" />
                        </button>
                    </PopoverTrigger>
                )}
                <PopoverContent
                    className="w-[var(--radix-popover-trigger-width)] p-0"
                    align="start"
                    ref={contentRef}
                    onPointerDownOutside={(e) => {
                        const target = e.target as Node | null;

                        if (
                            inputRef.current &&
                            target &&
                            inputRef.current.contains(target)
                        ) {
                            e.preventDefault();
                            return;
                        }

                        if (document.activeElement === inputRef.current) {
                            e.preventDefault();
                            return;
                        }
                    }}
                >
                    <div
                        className="sticky top-0 z-10 border-b bg-background p-2"
                        onPointerDown={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <div className="relative">
                            <Search className="absolute top-2.5 left-2 h-4 w-4 text-muted-foreground" />
                            <Input
                                ref={inputRef}
                                placeholder="Пошук товарів..."
                                className="h-8 pl-8 text-sm sm:h-9"
                                value={searchTerm}
                                onChange={handleInputChange}
                                onKeyDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                    </div>

                    <div className="max-h-[300px] overflow-auto p-1">
                        {options?.length > 0 ? (
                            options.map((product) => (
                                <div
                                    key={product.id}
                                    className={cn(
                                        'relative flex w-full cursor-default items-center rounded-xl py-1.5 pr-2 pl-8 text-base outline-none select-none hover:bg-accent hover:text-accent-foreground',
                                        'flex items-center py-2',
                                    )}
                                >
                                    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                                        {selectedProduct?.id === product.id && (
                                            <Check className="h-4 w-4" />
                                        )}
                                    </span>
                                    <div className="flex w-full cursor-pointer items-center gap-3">
                                        <div className="relative flex h-10 w-8 flex-shrink-0 items-center">
                                            <Image
                                                src={product.image}
                                                alt={product.image_alt}
                                                className="rounded object-cover"
                                                loading="lazy"
                                                onTouchStart={(event) => {
                                                    event.stopPropagation();
                                                    showProductImage(product, {
                                                        x: event.touches[0]
                                                            .clientX,
                                                        y: event.touches[0]
                                                            .clientY,
                                                    });
                                                }}
                                                onMouseMove={(event) =>
                                                    showProductImage(product, {
                                                        x: event.clientX,
                                                        y: event.clientY,
                                                    })
                                                }
                                                onMouseOut={() => {
                                                    setPreview(null);
                                                }}
                                            />
                                        </div>
                                        <div
                                            className="flex h-10 w-full min-w-0 items-center gap-1"
                                            onClick={() => {
                                                handleProductChange(product);
                                            }}
                                        >
                                            <p
                                                className="min-w-0 truncate text-sm"
                                                data-testid="product-option-name"
                                            >
                                                {product.name}
                                            </p>
                                            <Badge
                                                className="shrink-0"
                                                data-testid="product-option-expansion-tag"
                                            >
                                                {product.expansion_tag}
                                            </Badge>
                                            <Badge
                                                variant="outline"
                                                className="shrink-0"
                                                data-testid="product-option-language"
                                            >
                                                {product.language}
                                            </Badge>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="px-1 py-2">
                                <div className="mb-2 py-4 text-center text-sm text-muted-foreground">
                                    Товари не знайдено
                                </div>
                                <button
                                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-secondary/50 px-3 py-2 text-sm font-medium text-secondary-foreground transition-colors hover:bg-secondary/70"
                                    onClick={openProposalModal}
                                >
                                    <Plus className="h-4 w-4" />
                                    Запропонувати товар
                                </button>
                            </div>
                        )}
                    </div>
                </PopoverContent>
            </Popover>

            {isProposalOpen && (
                <ProductProposalModal
                    open={isProposalOpen}
                    onOpenChange={setIsProposalOpen}
                    defaultName={searchTerm}
                    onSuccess={() => setOpen(false)}
                />
            )}
        </>
    );
};
